import React, { useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { makeStyles } from 'tss-react/mui';
import { isMobileBrowser } from '../../../base/environment/utils';
import { isLocalParticipantModerator } from '../../../base/participants/functions';
import { NARROW_LAYOUT_THRESHOLD } from '../../../base/responsive-ui/constants';
import { setHangupMenuVisible, setToolbarHovered } from '../../../toolbox/actions.web';
import Drawer from '../../../toolbox/components/web/Drawer';
import HangupDrawerContent from '../../../toolbox/components/web/HangupDrawerContent';
import JitsiPortal from '../../../toolbox/components/web/JitsiPortal';
import { isButtonEnabled } from '../../../toolbox/functions.any';
/**
 * 窄屏/手机顶栏右上角挂断按钮：主持人显示「结束」，参会者显示「离开」；
 * 点击打开底部抽屉（离开会议 / 结束会议；点空白遮罩关闭）。
 *
 * @param {IProps} props - 组件属性。
 * @returns {ReactElement|null}
 */
const NarrowHangupButton = ({ variant = 'filled' }) => {
    const { classes, cx } = useStyles();
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const conference = useSelector((state) => state['features/base/conference'].conference);
    const hangupMenuVisible = useSelector((state) => state['features/toolbox'].hangupMenuVisible);
    const toolbarButtons = useSelector((state) => state['features/toolbox'].toolbarButtons);
    const buttonsWithNotifyClick = useSelector((state) => state['features/toolbox'].buttonsWithNotifyClick);
    const isModerator = useSelector(isLocalParticipantModerator);
    const endConferenceSupported = Boolean(conference?.isEndConferenceSupported() && isModerator);
    const isNarrowLayout = useSelector((state) => state['features/base/responsive-ui'].isNarrowLayout);
    const videoSpaceWidth = useSelector((state) => state['features/base/responsive-ui'].videoSpaceWidth);
    const isMobile = isMobileBrowser();
    const isWidthNarrow = videoSpaceWidth < NARROW_LAYOUT_THRESHOLD;
    const showNarrowHangup = isNarrowLayout || isMobile || isWidthNarrow;
    // 与桌面底栏一致：仅主持人显示「结束」，参会者显示「离开」
    const hangupShortLabelKey = endConferenceSupported ? 'toolbar.endShort' : 'toolbar.leaveShort';
    const hangupAriaLabelKey = endConferenceSupported
        ? 'toolbar.endMeeting'
        : 'toolbar.accessibilityLabel.hangup';
    const onSetHangupVisible = useCallback((visible) => {
        dispatch(setHangupMenuVisible(visible));
        dispatch(setToolbarHovered(visible));
    }, [dispatch]);
    const onToggle = useCallback(() => {
        onSetHangupVisible(!hangupMenuVisible);
    }, [hangupMenuVisible, onSetHangupVisible]);
    const onClose = useCallback(() => {
        onSetHangupVisible(false);
    }, [onSetHangupVisible]);
    const onEscKey = useCallback((e) => {
        if (e?.key === 'Escape') {
            e.stopPropagation();
            onClose();
        }
    }, [onClose]);
    if (!showNarrowHangup || !isButtonEnabled('hangup', toolbarButtons)) {
        return null;
    }
    return (React.createElement("div", { className: cx(classes.wrapper, variant === 'text' && classes.wrapperText) },
        React.createElement("button", { "aria-controls": 'narrow-hangup-menu', "aria-expanded": hangupMenuVisible, "aria-label": t(hangupAriaLabelKey), className: cx(classes.endButton, variant === 'text' && classes.endButtonText, hangupMenuVisible && classes.endButtonOpen), onClick: onToggle, type: 'button' }, t(hangupShortLabelKey)),
        React.createElement(JitsiPortal, null,
            React.createElement(Drawer, { isOpen: hangupMenuVisible, onClose: onClose },
                React.createElement(HangupDrawerContent, { buttonsWithNotifyClick: buttonsWithNotifyClick, endConferenceSupported: endConferenceSupported, id: 'narrow-hangup-menu', onKeyDown: onEscKey })))));
};
const useStyles = makeStyles()(theme => {
    return {
        wrapper: {
            alignItems: 'center',
            display: 'flex',
            flexShrink: 0,
            height: '100%',
            paddingRight: 12
        },
        wrapperText: {
            paddingRight: 4
        },
        endButton: {
            backgroundColor: theme.palette.hangupButtonBackground,
            border: 0,
            borderRadius: 6,
            color: '#FFFFFF',
            cursor: 'pointer',
            fontFamily: 'inherit',
            fontSize: '0.875rem',
            fontWeight: 600,
            lineHeight: 1.2,
            minWidth: 52,
            padding: '6px 12px',
            '@media (hover: hover) and (pointer: fine)': {
                '&:hover': {
                    backgroundColor: theme.palette.actionDangerHover
                }
            }
        },
        /** 手机顶栏：红色文字「结束」，无底色。 */
        endButtonText: {
            backgroundColor: 'transparent',
            color: theme.palette.hangupButtonBackground || '#E53935',
            fontSize: '1rem',
            fontWeight: 500,
            minWidth: 'auto',
            padding: '6px 8px',
            '@media (hover: hover) and (pointer: fine)': {
                '&:hover': {
                    backgroundColor: theme.palette.ui03,
                    color: theme.palette.actionDangerHover || '#C62828'
                }
            }
        },
        endButtonOpen: {
            opacity: 0.9
        }
    };
});
export default NarrowHangupButton;
